import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

const parse = (s) => {
    if (!s) return null;
    const d = new Date(s + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
};
const fmt = (d) =>
    d ? d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const MS_DAY = 86400000;

export default function ProjectGantt({ projects }) {
    const [search, setSearch] = useState("");

    const rows = useMemo(() => {
        const list = (projects || [])
            .map((p) => {
                const start = parse(p.date_start);
                const end = parse(p.date_end);
                const adj = parse(p.date_adjusted);
                return { ...p, _start: start, _end: end, _adj: adj };
            })
            .filter((p) => p._start && (p._end || p._adj));
        const q = search.trim().toLowerCase();
        return list
            .filter(
                (p) =>
                    !q ||
                    (p.name || "").toLowerCase().includes(q) ||
                    (p.project_code || "").toLowerCase().includes(q),
            )
            .sort((a, b) => a._start - b._start);
    }, [projects, search]);

    const { min, max, months } = useMemo(() => {
        if (!rows.length) return { min: null, max: null, months: [] };
        let mn = rows[0]._start;
        let mx = rows[0]._end || rows[0]._adj;
        for (const p of rows) {
            if (p._start < mn) mn = p._start;
            const e = p._adj && p._end ? (p._adj > p._end ? p._adj : p._end) : p._adj || p._end;
            if (e > mx) mx = e;
        }
        // padding a inicio de mes / fin de mes
        const start = new Date(mn.getFullYear(), mn.getMonth(), 1);
        const end = new Date(mx.getFullYear(), mx.getMonth() + 1, 0);
        const mths = [];
        let cur = new Date(start);
        while (cur <= end) {
            mths.push(new Date(cur));
            cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        }
        return { min: start, max: end, months: mths };
    }, [rows]);

    const span = min && max ? max - min : 1;
    const pct = (d) => `${((d - min) / span) * 100}%`;
    const widthPct = (a, b) => `${(Math.max(0, b - a) / span) * 100}%`;

    const today = useMemo(() => {
        const t = new Date();
        return new Date(t.getFullYear(), t.getMonth(), t.getDate());
    }, []);
    const todayInRange = min && max && today >= min && today <= max;

    if (!rows.length) {
        return (
            <div className="border border-slate-200 bg-white p-10 text-center text-slate-400 text-sm">
                No hay proyectos con fechas. Añade las fechas de inicio/fin editando un proyecto.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="gantt-view">
            <div className="flex items-center gap-3">
                <div className="relative w-72">
                    <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
                    <Input
                        placeholder="Buscar proyecto..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-sm border-slate-300 pl-7 h-8"
                        data-testid="gantt-search"
                    />
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-600 ml-auto">
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm bg-[#002BAF]" /> Planificado
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-sm bg-amber-400" /> Retraso (ajuste)
                    </span>
                    <span className="font-mono text-slate-400">{rows.length} proyectos</span>
                </div>
            </div>

            <div className="border border-slate-200 bg-white overflow-x-auto">
                <div className="min-w-[900px]">
                    {/* Cabecera de meses */}
                    <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0">
                        <div className="w-64 flex-shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-r border-slate-200">
                            Proyecto
                        </div>
                        <div className="relative flex-1 h-8">
                            {months.map((m, i) => (
                                <div
                                    key={i}
                                    className="absolute top-0 bottom-0 border-l border-slate-100 text-[10px] text-slate-400 pl-1 pt-1.5 whitespace-nowrap"
                                    style={{ left: pct(m) }}
                                >
                                    {m.toLocaleDateString("es-ES", {
                                        month: "short",
                                        year: "2-digit",
                                    })}
                                </div>
                            ))}
                            {todayInRange && (
                                <div
                                    className="absolute top-0 bottom-0 z-20"
                                    style={{ left: pct(today) }}
                                >
                                    <div className="w-0.5 h-full bg-rose-500" />
                                    <span className="absolute top-0 left-1 text-[9px] font-semibold text-rose-600">
                                        hoy
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {rows.map((p) => {
                        const end = p._end || p._adj;
                        const hasExt = p._adj && p._end && p._adj > p._end;
                        return (
                            <div
                                key={p.project_id}
                                className="flex items-center border-b border-slate-100 hover:bg-slate-50"
                                data-testid={`gantt-row-${p.project_id}`}
                            >
                                <div className="w-64 flex-shrink-0 px-3 py-2 border-r border-slate-100">
                                    <div className="text-sm text-slate-800 truncate font-medium">
                                        {p.name}
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-mono">
                                        {p.project_code || "—"} · {fmt(p._start)} →{" "}
                                        {fmt(hasExt ? p._adj : end)}
                                    </div>
                                </div>
                                <div className="relative flex-1 h-10">
                                    {/* barra planificada */}
                                    <div
                                        className="absolute top-1/2 -translate-y-1/2 h-4 rounded-sm bg-[#002BAF]"
                                        style={{
                                            left: pct(p._start),
                                            width: widthPct(p._start, end),
                                        }}
                                        title={`Planificado: ${fmt(p._start)} → ${fmt(end)}`}
                                    />
                                    {/* extensión por retraso */}
                                    {hasExt && (
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2 h-4 rounded-r-sm bg-amber-400"
                                            style={{
                                                left: pct(p._end),
                                                width: widthPct(p._end, p._adj),
                                            }}
                                            title={`Retraso hasta ${fmt(p._adj)}`}
                                        />
                                    )}
                                    {/* línea de hoy */}
                                    {todayInRange && (
                                        <div
                                            className="absolute top-0 bottom-0 w-0.5 bg-rose-500/70 z-10 pointer-events-none"
                                            style={{ left: pct(today) }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
